// src/components/ResumeForm.js
//
// Formulário de cadastro/edição do currículo (Fase G.1.2).
// Persiste em Firestore na coleção `resumes` (id = uid do usuário) e o
// anexo de CV (PDF ou DOC) no Storage em /resumes/<uid>/cv.<ext>.
import React, { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { db, storage } from '../firebaseConfig';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import Button from './Button/Button';
import H2 from './H2/H2';
import H3 from './H3/H3';
import MessagePopup from './MessagePopup/MessagePopup';
import {
  initialResume,
  CATEGORY_LABELS,
  EDUCATION_LEVELS,
  LANGUAGE_LEVELS,
  AVAILABILITY_TYPES,
  BRAZILIAN_STATES,
  ALLOWED_ATTACHMENT_TYPES,
  MAX_ATTACHMENT_SIZE_BYTES,
} from '../utils/resumeConstants';

const getExtension = (file) => {
  const fromName = file.name && file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (fromName) return fromName;
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'application/msword') return 'doc';
  if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
  return 'bin';
};

const ResumeForm = ({ userId }) => {
  const [resume, setResume] = useState(initialResume);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [message, setMessage] = useState(null);
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    const fetchResume = async () => {
      if (!userId) return;
      try {
        const ref0 = doc(db, 'resumes', userId);
        const snap = await getDoc(ref0);
        if (snap.exists()) {
          setResume({ ...initialResume, ...snap.data() });
        } else {
          // Pré-preenche com dados do usuário, se disponíveis.
          const userSnap = await getDoc(doc(db, 'users', userId));
          if (userSnap.exists()) {
            const u = userSnap.data();
            setResume((prev) => ({
              ...prev,
              userId,
              fullName: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
              email: u.email || '',
              phone: u.mobile || '',
              nationality: u.nationality || '',
              country: u.address?.country || 'Brasil',
              state: u.address?.state || '',
              city: u.address?.city || '',
            }));
          } else {
            setResume((prev) => ({ ...prev, userId }));
          }
        }
      } catch (err) {
        console.error('Erro ao carregar currículo:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchResume();
  }, [userId]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setResume((prev) => ({ ...prev, [field]: value }));
  };

  const updateAvailability = (key, value) => {
    setResume((prev) => ({
      ...prev,
      availability: { ...prev.availability, [key]: value },
    }));
  };

  const toggleAvailabilityType = (key) => {
    setResume((prev) => {
      const current = Array.isArray(prev.availability?.types) ? prev.availability.types : [];
      const next = current.includes(key) ? current.filter((c) => c !== key) : [...current, key];
      return { ...prev, availability: { ...prev.availability, types: next } };
    });
  };

  const addEducation = () => {
    setResume((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        { institution: '', course: '', level: '', startYear: '', endYear: '', description: '' },
      ],
    }));
  };

  const updateEducationField = (idx, field, value) => {
    setResume((prev) => {
      const next = [...prev.education];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, education: next };
    });
  };

  const removeEducation = (idx) => {
    setResume((prev) => ({ ...prev, education: prev.education.filter((_, i) => i !== idx) }));
  };

  const addExperience = () => {
    setResume((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        { company: '', role: '', startMonth: '', endMonth: '', current: false, description: '' },
      ],
    }));
  };

  const updateExperienceField = (idx, field, value) => {
    setResume((prev) => {
      const next = [...prev.experience];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'current' && value) next[idx].endMonth = '';
      return { ...prev, experience: next };
    });
  };

  const removeExperience = (idx) => {
    setResume((prev) => ({ ...prev, experience: prev.experience.filter((_, i) => i !== idx) }));
  };

  const addLanguage = () => {
    setResume((prev) => ({
      ...prev,
      languages: [...prev.languages, { name: '', level: 'basico' }],
    }));
  };

  const updateLanguageField = (idx, field, value) => {
    setResume((prev) => {
      const next = [...prev.languages];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, languages: next };
    });
  };

  const removeLanguage = (idx) => {
    setResume((prev) => ({ ...prev, languages: prev.languages.filter((_, i) => i !== idx) }));
  };

  const addSkill = () => {
    const trimmed = skillInput.trim();
    if (!trimmed) return;
    if (resume.skills.includes(trimmed)) {
      setSkillInput('');
      return;
    }
    setResume((prev) => ({ ...prev, skills: [...prev.skills, trimmed] }));
    setSkillInput('');
  };

  const removeSkill = (skill) => {
    setResume((prev) => ({ ...prev, skills: prev.skills.filter((s) => s !== skill) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!userId) return;
    setSaving(true);
    try {
      const docRef = doc(db, 'resumes', userId);
      await setDoc(
        docRef,
        { ...resume, userId, updatedAt: serverTimestamp(), createdAt: resume.createdAt || serverTimestamp() },
        { merge: true }
      );
      setMessage({ title: 'Currículo', body: 'Currículo salvo com sucesso!' });
    } catch (err) {
      console.error('Erro ao salvar currículo:', err);
      setMessage({ title: 'Currículo', body: 'Erro ao salvar o currículo.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentUpload = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      setMessage({ title: 'Currículo', body: 'Formato inválido. Use PDF, DOC ou DOCX.' });
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setMessage({ title: 'Currículo', body: 'O arquivo deve ter no máximo 5 MB.' });
      return;
    }

    const extension = getExtension(file);
    const storageRef = ref(storage, `resumes/${userId}/cv.${extension}`);
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      cacheControl: 'private, max-age=3600',
    });

    setUploadProgress(0);
    task.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (err) => {
        console.error('Erro de upload:', err);
        setUploadProgress(null);
        setMessage({ title: 'Currículo', body: 'Erro ao enviar o arquivo.' });
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const previousUrl = resume.attachmentUrl;
          const newResume = {
            ...resume,
            attachmentUrl: url,
            attachmentName: file.name,
          };
          setResume(newResume);
          await setDoc(
            doc(db, 'resumes', userId),
            { ...newResume, userId, updatedAt: serverTimestamp() },
            { merge: true }
          );

          if (previousUrl && previousUrl.includes('/o/resumes%2F') && !previousUrl.includes(`cv.${extension}`)) {
            try {
              const oldPath = decodeURIComponent(previousUrl.split('/o/')[1].split('?')[0]);
              await deleteObject(ref(storage, oldPath));
            } catch (cleanupErr) {
              console.warn('Não foi possível remover o anexo anterior:', cleanupErr);
            }
          }
          setMessage({ title: 'Currículo', body: 'Anexo atualizado com sucesso!' });
        } catch (err) {
          console.error('Erro ao salvar URL do anexo:', err);
          setMessage({ title: 'Currículo', body: 'Erro ao salvar o anexo.' });
        } finally {
          setUploadProgress(null);
        }
      }
    );
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto p-5">Carregando currículo...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-5 py-10">
      <H2 text="Meu Currículo" textColor="purple" />

      <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 mt-4 shadow-sm border border-gray-200">
        {/* Visibilidade */}
        <section className="mb-8">
          <H3 text="Visibilidade" textColor="purple" />
          <p className="text-sm text-gray-600 -mt-3 mb-3">
            Currículos públicos aparecem na busca de empregadores. Você pode mudar isso a qualquer momento.
          </p>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={resume.visibility === 'public'}
                onChange={() => setResume((p) => ({ ...p, visibility: 'public' }))}
              />
              Tornar meu currículo público
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={resume.visibility !== 'public'}
                onChange={() => setResume((p) => ({ ...p, visibility: 'private' }))}
              />
              Manter privado
            </label>
          </div>
        </section>

        {/* Dados pessoais */}
        <section className="mb-8">
          <H3 text="Dados pessoais" textColor="purple" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block text-sm">
              Nome completo
              <input type="text" value={resume.fullName} onChange={handleChange('fullName')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
            </label>
            <label className="block text-sm">
              E-mail
              <input type="email" value={resume.email} onChange={handleChange('email')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
            </label>
            <label className="block text-sm">
              Telefone
              <InputMask mask="+55 (99) 99999-9999" value={resume.phone} onChange={handleChange('phone')}>
                {(inputProps) => (
                  <input {...inputProps} type="text" className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                )}
              </InputMask>
            </label>
            <label className="block text-sm">
              Nacionalidade
              <input type="text" value={resume.nationality} onChange={handleChange('nationality')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
            </label>
            <label className="block text-sm">
              País
              <input type="text" value={resume.country} onChange={handleChange('country')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
            </label>
            <label className="block text-sm">
              Estado
              {resume.country === 'Brasil' ? (
                <select value={resume.state} onChange={handleChange('state')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1">
                  <option value="">Selecione</option>
                  {BRAZILIAN_STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              ) : (
                <input type="text" value={resume.state} onChange={handleChange('state')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
              )}
            </label>
            <label className="block text-sm">
              Cidade
              <input type="text" value={resume.city} onChange={handleChange('city')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
            </label>
            <label className="block text-sm md:col-span-2">
              Resumo profissional (uma frase)
              <input type="text" maxLength={120} value={resume.headline} onChange={handleChange('headline')} placeholder="Ex.: Cozinheiro com 10 anos de experiência" className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
            </label>
            <label className="block text-sm md:col-span-3">
              Sobre você
              <textarea value={resume.about} onChange={handleChange('about')} maxLength={1000} rows={4} className="w-full border border-gray-300 rounded px-2 py-2 mt-1" />
              <span className="text-xs text-gray-500">{resume.about.length}/1000</span>
            </label>
            <label className="block text-sm">
              Área principal
              <select value={resume.category} onChange={handleChange('category')} className="w-full h-10 border border-gray-300 rounded px-2 mt-1">
                <option value="">Selecione</option>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </div>
        </section>

        {/* Formação */}
        <section className="mb-8">
          <div className="flex items-center justify-between">
            <H3 text="Formação acadêmica" textColor="purple" />
            <button type="button" onClick={addEducation} className="text-sm text-purple-refugia hover:underline">
              + Adicionar formação
            </button>
          </div>
          {resume.education.length === 0 && <p className="text-sm text-gray-500">Nenhuma formação cadastrada.</p>}
          {resume.education.map((edu, idx) => (
            <div key={idx} className="border border-gray-200 rounded p-3 mb-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block text-sm md:col-span-2">
                  Instituição
                  <input type="text" value={edu.institution} onChange={(e) => updateEducationField(idx, 'institution', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <label className="block text-sm">
                  Nível
                  <select value={edu.level} onChange={(e) => updateEducationField(idx, 'level', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1">
                    <option value="">Selecione</option>
                    {Object.entries(EDUCATION_LEVELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </label>
                <label className="block text-sm md:col-span-3">
                  Curso
                  <input type="text" value={edu.course} onChange={(e) => updateEducationField(idx, 'course', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <label className="block text-sm">
                  Ano início
                  <input type="number" min="1950" max="2100" inputMode="numeric" placeholder="AAAA" value={edu.startYear} onChange={(e) => updateEducationField(idx, 'startYear', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <label className="block text-sm">
                  Ano fim
                  <input type="number" min="1950" max="2100" inputMode="numeric" placeholder="AAAA" value={edu.endYear} onChange={(e) => updateEducationField(idx, 'endYear', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <div className="flex items-end">
                  <button type="button" onClick={() => removeEducation(idx)} className="text-sm text-pink-600 hover:underline">
                    Remover
                  </button>
                </div>
                <label className="block text-sm md:col-span-3">
                  Descrição (opcional)
                  <textarea value={edu.description} onChange={(e) => updateEducationField(idx, 'description', e.target.value)} rows={2} maxLength={400} className="w-full border border-gray-300 rounded px-2 py-2 mt-1" />
                </label>
              </div>
            </div>
          ))}
        </section>

        {/* Experiência */}
        <section className="mb-8">
          <div className="flex items-center justify-between">
            <H3 text="Experiência profissional" textColor="purple" />
            <button type="button" onClick={addExperience} className="text-sm text-purple-refugia hover:underline">
              + Adicionar experiência
            </button>
          </div>
          {resume.experience.length === 0 && <p className="text-sm text-gray-500">Nenhuma experiência cadastrada.</p>}
          {resume.experience.map((exp, idx) => (
            <div key={idx} className="border border-gray-200 rounded p-3 mb-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block text-sm">
                  Empresa
                  <input type="text" value={exp.company} onChange={(e) => updateExperienceField(idx, 'company', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <label className="block text-sm md:col-span-2">
                  Cargo
                  <input type="text" value={exp.role} onChange={(e) => updateExperienceField(idx, 'role', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <label className="block text-sm">
                  Início (mês/ano)
                  <input type="month" value={exp.startMonth} onChange={(e) => updateExperienceField(idx, 'startMonth', e.target.value)} className="w-full h-10 border border-gray-300 rounded px-2 mt-1" />
                </label>
                <label className="block text-sm">
                  Fim (mês/ano)
                  <input type="month" value={exp.endMonth} onChange={(e) => updateExperienceField(idx, 'endMonth', e.target.value)} disabled={exp.current} className="w-full h-10 border border-gray-300 rounded px-2 mt-1 disabled:bg-gray-100" />
                </label>
                <label className="flex items-center gap-2 text-sm mt-7">
                  <input type="checkbox" checked={Boolean(exp.current)} onChange={(e) => updateExperienceField(idx, 'current', e.target.checked)} />
                  Atual
                </label>
                <label className="block text-sm md:col-span-3">
                  Descrição
                  <textarea value={exp.description} onChange={(e) => updateExperienceField(idx, 'description', e.target.value)} rows={2} maxLength={600} className="w-full border border-gray-300 rounded px-2 py-2 mt-1" />
                </label>
                <div className="md:col-span-3">
                  <button type="button" onClick={() => removeExperience(idx)} className="text-sm text-pink-600 hover:underline">
                    Remover experiência
                  </button>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Idiomas */}
        <section className="mb-8">
          <div className="flex items-center justify-between">
            <H3 text="Idiomas" textColor="purple" />
            <button type="button" onClick={addLanguage} className="text-sm text-purple-refugia hover:underline">
              + Adicionar idioma
            </button>
          </div>
          {resume.languages.length === 0 && <p className="text-sm text-gray-500">Nenhum idioma cadastrado.</p>}
          {resume.languages.map((lang, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
              <input
                type="text"
                placeholder="Idioma (ex.: Inglês)"
                value={lang.name}
                onChange={(e) => updateLanguageField(idx, 'name', e.target.value)}
                className="h-10 border border-gray-300 rounded px-2"
              />
              <select value={lang.level} onChange={(e) => updateLanguageField(idx, 'level', e.target.value)} className="h-10 border border-gray-300 rounded px-2">
                {Object.entries(LANGUAGE_LEVELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button type="button" onClick={() => removeLanguage(idx)} className="text-sm text-pink-600 hover:underline text-left">Remover</button>
            </div>
          ))}
        </section>

        {/* Habilidades */}
        <section className="mb-8">
          <H3 text="Habilidades" textColor="purple" />
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
              placeholder="Digite uma habilidade e pressione Enter"
              className="flex-1 h-10 border border-gray-300 rounded px-2"
            />
            <button type="button" onClick={addSkill} className="bg-purple-refugia text-white px-4 rounded">Adicionar</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {resume.skills.map((skill) => (
              <span key={skill} className="bg-gray-100 border border-gray-300 rounded-full px-3 py-1 text-sm flex items-center gap-1">
                {skill}
                <button type="button" onClick={() => removeSkill(skill)} className="text-gray-500 hover:text-pink-600" aria-label={`Remover ${skill}`}>×</button>
              </span>
            ))}
          </div>
        </section>

        {/* Disponibilidade */}
        <section className="mb-8">
          <H3 text="Disponibilidade" textColor="purple" />
          <div className="flex flex-wrap gap-2 mb-2">
            {Object.entries(AVAILABILITY_TYPES).map(([k, v]) => {
              const selected = Array.isArray(resume.availability?.types) && resume.availability.types.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleAvailabilityType(k)}
                  className={`px-3 py-1 rounded-full border text-sm ${selected ? 'bg-purple-refugia text-white border-purple-refugia' : 'bg-white text-gray-700 border-gray-300'}`}
                >
                  {v}
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(resume.availability?.immediate)} onChange={(e) => updateAvailability('immediate', e.target.checked)} />
              Disponibilidade imediata
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(resume.availability?.remote)} onChange={(e) => updateAvailability('remote', e.target.checked)} />
              Aceito trabalho remoto
            </label>
          </div>
        </section>

        {/* Anexo */}
        <section className="mb-8">
          <H3 text="Currículo em PDF (opcional)" textColor="purple" />
          {resume.attachmentUrl && (
            <p className="text-sm mb-2">
              Arquivo atual:{' '}
              <a href={resume.attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                {resume.attachmentName || 'currículo'}
              </a>
            </p>
          )}
          <input
            type="file"
            accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
            onChange={handleAttachmentUpload}
            disabled={uploadProgress !== null}
            className="text-sm"
          />
          {uploadProgress !== null && (
            <div className="mt-2 w-60">
              <div className="h-2 w-full bg-gray-200 rounded">
                <div className="h-2 bg-purple-refugia rounded transition-all" style={{ width: `${Math.round(uploadProgress)}%` }} />
              </div>
              <p className="text-xs text-gray-600 mt-1">Enviando… {Math.round(uploadProgress)}%</p>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">Máximo 5 MB (PDF, DOC ou DOCX).</p>
        </section>

        <div className="flex justify-end">
          <Button text={saving ? 'Salvando...' : 'Salvar currículo'} variant="transparent" type="submit" disabled={saving} />
        </div>
      </form>

      {message && (
        <MessagePopup title={message.title} message={message.body} onClose={() => setMessage(null)} />
      )}
    </div>
  );
};

export default ResumeForm;
